/**
 * features/hr/useClockingRules.ts
 * ------------------------------------------------------------------
 * The school's clocking rules (lib/clockingRules) as they stand today:
 *   - the LIVE public holiday list, so a holiday HR adds on the Public
 *     Holidays screen stops anyone being late or "not in" on it — the
 *     same list leave counting uses;
 *   - the school's term dates, outside which nobody is late either;
 *   - each staff type's own start times (hrClockingSettings/school),
 *     edited on the Clocking screen — `rulesFor(category)`.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import {
  SCHOOL_CLOCKING, CLOCKING_SETTINGS_COLLECTION, CLOCKING_SETTINGS_DOC, rulesForCategory,
  type CategoryStarts, type ClockingRules,
} from '@/lib/clockingRules';
import type { StaffCategory } from '@/lib/hr';
import { usePublicHolidays } from '@/features/hr/usePublicHolidays';
import { useData } from '@/contexts/DataContext';
import { isInTerm } from '@/lib/termDates';

export interface LiveClockingRules extends ClockingRules {
  holidayName: (date: string) => string | undefined;
  /** Start times as saved per staff type (may be partial). */
  starts: CategoryStarts;
  /** The rules for one staff type. */
  rulesFor: (category: StaffCategory | undefined) => ClockingRules;
}

export function useClockingRules(): LiveClockingRules {
  const { isHoliday, holidayName } = usePublicHolidays();
  // Term dates as set on the Term Dates screen, falling back to the built-in ones.
  const { termCalendar } = useData();
  const [starts, setStarts] = useState<CategoryStarts>({});
  useEffect(() => onSnapshot(
    doc(db, CLOCKING_SETTINGS_COLLECTION, CLOCKING_SETTINGS_DOC),
    (snap) => setStarts(((snap.data() as { starts?: CategoryStarts } | undefined)?.starts) ?? {}),
    () => setStarts({}),
  ), []);

  return useMemo(() => {
    const base: ClockingRules = { ...SCHOOL_CLOCKING, isHoliday, inTerm: (d: string) => isInTerm(d, termCalendar) };
    return { ...base, holidayName, starts, rulesFor: (c) => rulesForCategory(base, starts, c) };
  }, [isHoliday, holidayName, termCalendar, starts]);
}
